import type { IpcRendererEvent } from "electron";

import { DownloadableAsset } from "../model/Asset";
import AssetDownloadTarget from "../model/AssetDownloadTarget";
import showToast from "../utils/showToast";
import { PLATFORM } from "../enums";

import electron from "./electronMirror";
import { AssetContentFormat } from "../api/model/snapshots/Asset";

type DownloadTargetHandler = (downloadTargets: AssetDownloadTarget[]) => void;

// This is how old electron apps (<= 10.7.0) used to send download targets
type OldDownloadTarget = {
    projectFolder: string;
    projectFolderType: string | undefined;
    projectName: string;
};

let currentPlatform: PLATFORM | undefined;
let downloadTargetHandler: DownloadTargetHandler | undefined;
let toastTimeout: ReturnType<typeof setTimeout>;

function setupQueryAssetDownloadTargetHandler(platform: PLATFORM) {
    if (currentPlatform && currentPlatform !== platform) {
        electron!.actions.unregisterAssetDownloadTargetListeners(currentPlatform);
    }

    currentPlatform = platform;

    electron!.actions.unregisterAssetDownloadTargetListeners(platform);

    electron!.actions.onAssetDownloadTarget(platform, (_, downloadTargets) => {
        downloadTargetHandler!(downloadTargets);
    });
}

function handleAssetsDownloaded(downloadTarget: AssetDownloadTarget | OldDownloadTarget, toastContainer: HTMLElement) {
    clearTimeout(toastTimeout);

    const targetName = "name" in downloadTarget ? downloadTarget.name : downloadTarget.projectName;

    // TODO-yahya: Project name could be too long. We should somehow ellipsis it in either here or in the toast component
    toastTimeout = setTimeout(() => {
        showToast({
            text: `Exported to ${targetName}`,
            container: toastContainer,
            source: "assetDownloadTarget"
        });
    }, 200);
}

export function queryAssetDownloadTarget(platform: PLATFORM, handler: DownloadTargetHandler) {
    downloadTargetHandler = handler;
    setupQueryAssetDownloadTargetHandler(platform);
    electron!.actions.sendQueryAssetDownloadTarget(platform);
}

export function downloadAssets(data: {
    platform: PLATFORM;
    format: AssetContentFormat;
    assets: DownloadableAsset[];
    namingConvention: string;
    toastContainer: HTMLElement;
    downloadTarget?: AssetDownloadTarget;
    shouldBrowseNewFolder?: boolean;
}) {
    const { toastContainer, ...rest } = data;

    electron!.actions.unregisterAssetsDownloadedListeners();
    electron!.actions.onceAssetsDownloaded((_: IpcRendererEvent, result: AssetDownloadTarget) =>
        handleAssetsDownloaded(result, toastContainer)
    );

    electron!.actions.sendDownloadAssets({
        ...rest,
        // Don't remove these fields, they are used in the old version (<= 10.7.0)
        projectType: data.platform,
        ignoreProjectFolderPersistence: true // It makes the older apps to always ask for the download location
    });
}

export function openAssetDownloadTarget(downloadTarget: AssetDownloadTarget) {
    electron!.actions.sendShowItemInFolder(downloadTarget.path);
}

export function deleteAssetDownloadTarget(downloadTarget: AssetDownloadTarget) {
    electron!.actions.sendDeleteAssetDownloadTarget(currentPlatform!, downloadTarget.id);
}
